import { Info, Lock, Play } from 'lucide-react';
import { airingLine, badgeOfKind, isLive, looseBadges, monogram, primaryAction } from '@/lib/card';
import type { MediaCard } from '@/protocol/media';
import { Pill } from '../ui';

/** The asked-for title, given room: art on the left, everything the payload carried on the right. */
export function Spotlight({
  card,
  onSelect,
}: {
  card: MediaCard;
  onSelect: (card: MediaCard) => void;
}) {
  const locked = card.playable === false;
  const entitlement = badgeOfKind(card, 'entitlement');
  const airing = airingLine(card);
  const action = primaryAction(card);

  return (
    <article className="border-line bg-surface/70 rounded-panel relative overflow-hidden border">
      {card.posterUrl && (
        <>
          <img
            src={card.posterUrl}
            alt=""
            className="pointer-events-none absolute inset-0 size-full object-cover opacity-25 blur-2xl"
          />
          <span className="from-shell/95 via-shell/70 pointer-events-none absolute inset-0 bg-gradient-to-r to-transparent" />
        </>
      )}

      <div className="relative flex flex-col gap-4 p-4 sm:flex-row sm:p-5">
        <div className="border-line bg-raised aspect-video w-full shrink-0 overflow-hidden rounded-xl border sm:w-[280px]">
          {card.posterUrl ? (
            <img src={card.posterUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="text-faint grid size-full place-items-center text-3xl font-semibold tracking-widest">
              {monogram(card)}
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {isLive(card) && <Pill tone="live">Live</Pill>}
            {entitlement && <Pill tone={locked ? 'locked' : 'positive'}>{entitlement.text}</Pill>}
            {looseBadges(card).map((badge) => (
              <Pill key={`${badge.kind}-${badge.text}`}>{badge.text}</Pill>
            ))}
          </div>

          <h3 className="text-xl leading-tight font-semibold tracking-tight">{card.title}</h3>
          {card.subtitle && <p className="text-muted text-sm">{card.subtitle}</p>}
          {airing && <p className="text-accent-soft text-sm font-medium">{airing}</p>}

          {card.synopsis && (
            <p className="text-muted line-clamp-3 text-sm leading-relaxed">{card.synopsis}</p>
          )}

          {card.rankReason && (
            <p className="text-faint text-xs italic">Why this: {card.rankReason}</p>
          )}

          <div className="mt-1 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onSelect(card)}
              className="bg-accent flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition hover:brightness-110"
            >
              {locked ? <Lock className="size-4" /> : <Play className="size-4 fill-current" />}
              {action?.label ?? (locked ? 'Locked' : 'Play')}
            </button>
            <button
              type="button"
              onClick={() => onSelect(card)}
              className="border-line text-muted hover:text-text hover:border-line flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition"
            >
              <Info className="size-4" />
              Details
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
